// projectIssuesAdapter — parse the `projectIssues` reply at the boundary
// (`Packaging.Issues`). Pure. An unknown row kind, or a known one missing
// what it names, reads as `unknown` (a generic row, still counted); an
// unknown whole is malformed. At most `ISSUE_ROWS_MAX` rows are kept
// whatever the reply holds, and `total` never reads below them.

export { ISSUE_ROWS_MAX, parseProjectIssues };

import { Result } from "@bilda/utils";
import { type ExternalError, mkMalformed } from "./externalError.js";
import type {
    IssuePlace,
    ProjectIssue,
    ProjectIssues,
} from "./ports/project.js";
import { fields, list, num, str } from "./wireShape.js";

// The server returns at most 100 rows; a reply with more is cut here.
const ISSUE_ROWS_MAX = 100;

const placeOf = (raw: unknown): IssuePlace | undefined => {
    const f = fields(raw);
    const id = str(f?.get("resourceId")) ?? str(f?.get("id"));
    return id === undefined
        ? undefined
        : { id, title: str(f?.get("title")) ?? id };
};

const placesOf = (raw: unknown): readonly IssuePlace[] =>
    list(raw).flatMap((p) => placeOf(p) ?? []);

// Present only when the reply names one.
const inContainer = (raw: unknown): { readonly container?: IssuePlace } => {
    const container = placeOf(raw);
    return container === undefined ? {} : { container };
};

const unknownIssue: ProjectIssue = { type: "unknown" };

const issueOf = (raw: unknown): ProjectIssue => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    switch (type) {
        case "issue_parts_planned":
        case "issue_parts_empty":
        case "issue_parts_removed": {
            const container = placeOf(f?.get("container"));
            const parts = placesOf(f?.get("parts"));
            return container === undefined || parts.length === 0
                ? unknownIssue
                : { type, container, parts };
        }
        case "issue_objectives_not_covered": {
            const g = fields(f?.get("goal"));
            const id = str(g?.get("id"));
            const objectives = list(f?.get("objectives")).flatMap(
                (o) => str(fields(o)?.get("statement")) ?? [],
            );
            return id === undefined || objectives.length === 0
                ? unknownIssue
                : {
                      type,
                      goal: { id, title: str(g?.get("title")) ?? id },
                      objectives,
                  };
        }
        case "issue_draft_unreviewed": {
            const part = placeOf(f?.get("part"));
            return part === undefined
                ? unknownIssue
                : { type, part, ...inContainer(f?.get("container")) };
        }
        case "issue_proposals_open": {
            const material = placeOf(f?.get("material"));
            const count = num(f?.get("count"));
            return material === undefined || count === undefined || count < 1
                ? unknownIssue
                : {
                      type,
                      material,
                      count: Math.floor(count),
                      ...inContainer(f?.get("container")),
                  };
        }
        default:
            return unknownIssue;
    }
};

const parseProjectIssues = (
    raw: unknown,
): Result.Result<ProjectIssues, ExternalError> => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    switch (type) {
        case "project_nothing_yet":
        case "project_ready":
            return Result.ok({ type });
        case "project_has_issues": {
            const issues = list(f?.get("issues"))
                .slice(0, ISSUE_ROWS_MAX)
                .map(issueOf);
            const total = Math.max(
                Math.floor(num(f?.get("total")) ?? 0),
                issues.length,
            );
            return issues.length === 0
                ? Result.err(mkMalformed("projectIssues returned no issue"))
                : Result.ok({ type, total, issues });
        }
        default:
            return Result.err(
                mkMalformed("projectIssues returned no known answer"),
            );
    }
};
